import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { todayISO } from "@/lib/utils";
import { periodKey, periodLabel, earliestRelevantDate } from "@/lib/checklist-period";
import { useConfirm } from "@/components/useConfirm";

interface Checklist { id: string; name: string; description: string | null; frequency: string; assigned_to_roles: string[]; assigned_to_department: string | null; active: boolean; }
interface ChItem { id: string; checklist_id: string; label: string; sort_order: number; required: boolean; }
interface Completion { id: string; item_id: string; employee_id: string; completed_on: string; period_key: string; }

export function Checklists() {
  const { employee } = useAuth();
  const [lists, setLists] = useState<Checklist[]>([]);
  const [items, setItems] = useState<ChItem[]>([]);
  const [done, setDone] = useState<Completion[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Local, not UTC: a checklist belongs to a working day (DATE-001).
  const todayStr = todayISO();

  useEffect(() => { if (employee) void load(); }, [employee?.id]);

  // HQ-CHECKLIST-001. This used to fetch only completions dated today, which
  // silently discarded everything a weekly list had already collected this week
  // and made a `once` list reappear daily. Completions are fetched for the
  // occurrences currently open instead: back to the start of the business week
  // when a weekly list is on screen, plus every `once` completion regardless of
  // age, because that occurrence never ends.
  async function load() {
    if (!employee) return;
    setError(null);
    const [{ data: l, error: lErr }, { data: i }] = await Promise.all([
      supabase.from("hq_checklists").select("*").eq("active", true).order("name"),
      supabase.from("hq_checklist_items").select("*").order("sort_order"),
    ]);
    if (lErr) { setError(lErr.message); return; }
    const lists = (l as Checklist[]) ?? [];
    setLists(lists);
    setItems((i as ChItem[]) ?? []);

    const since = earliestRelevantDate(lists.map(x => x.frequency));
    const queries = [
      supabase.from("hq_checklist_completions").select("*")
        .eq("employee_id", employee.id).gte("completed_on", since),
    ];
    if (lists.some(x => x.frequency === "once")) {
      queries.push(
        supabase.from("hq_checklist_completions").select("*")
          .eq("employee_id", employee.id).eq("period_key", "once"));
    }
    const results = await Promise.all(queries);
    const merged = new Map<string, Completion>();
    for (const r of results) for (const c of ((r.data as Completion[]) ?? [])) merged.set(c.id, c);
    setDone([...merged.values()]);
  }

  async function toggle(item: ChItem, checked: boolean, frequency: string) {
    if (!employee) return;
    setError(null);
    const key = periodKey(frequency);
    if (checked) {
      const { data, error: err } = await supabase.from("hq_checklist_completions").insert({
        checklist_id: item.checklist_id, item_id: item.id, employee_id: employee.id,
        completed_on: todayStr, period_key: key,
      }).select().single();
      if (err) { setError(err.message); return; }
      if (data) setDone(prev => [...prev, data as Completion]);
    } else {
      // Remove the completion for THIS occurrence, which for a weekly list may
      // have been ticked on an earlier day.
      const c = done.find(x => x.item_id === item.id && x.period_key === key);
      if (!c) return;
      const { error: err } = await supabase.from("hq_checklist_completions").delete().eq("id", c.id);
      if (err) { setError(err.message); return; }
      setDone(prev => prev.filter(x => x.id !== c.id));
    }
  }

  const itemsByList = useMemo(() => {
    const m: Record<string, ChItem[]> = {};
    for (const it of items) (m[it.checklist_id] = m[it.checklist_id] || []).push(it);
    return m;
  }, [items]);

  /** Items satisfied for a given checklist's current occurrence. */
  function doneFor(frequency: string): Set<string> {
    const key = periodKey(frequency);
    return new Set(done.filter(d => d.period_key === key).map(d => d.item_id));
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Your Checklists ✅</h1>
      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}

      {lists.length === 0 ? (
        <div className="card py-12 text-center text-sm text-gray2">No checklists assigned to your role yet.</div>
      ) : lists.map(l => {
        const its = itemsByList[l.id] ?? [];
        const doneSet = doneFor(l.frequency);
        const completed = its.filter(it => doneSet.has(it.id)).length;
        const pct = its.length ? Math.round((completed / its.length) * 100) : 0;
        return (
          <div key={l.id} className="card">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{l.name}</h2>
                {l.description && <p className="text-xs text-gray2">{l.description}</p>}
                {/* Which occurrence is being ticked off — a weekly list that
                    says "Today" is what made the old behaviour invisible. */}
                <p className="mt-0.5 text-xs font-medium text-teal-dark">{periodLabel(l.frequency)}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-teal-dark">{pct}%</div>
                <div className="text-xs text-gray2">{completed}/{its.length}</div>
              </div>
            </div>
            <ul className="space-y-1">
              {its.map(it => (
                <li key={it.id}>
                  <label className="flex items-center gap-3 rounded-lg px-2 py-2 cursor-pointer hover:bg-offwhite">
                    <input type="checkbox" checked={doneSet.has(it.id)} onChange={e => toggle(it, e.target.checked, l.frequency)} className="h-5 w-5" />
                    <span className={doneSet.has(it.id) ? "line-through text-gray2" : ""}>{it.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function ChecklistsAdmin() {
  const [lists, setLists] = useState<Checklist[]>([]);
  const [items, setItems] = useState<ChItem[]>([]);
  const [editing, setEditing] = useState<Partial<Checklist> | null>(null);
  const [openItems, setOpenItems] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: l }, { data: i }] = await Promise.all([
      supabase.from("hq_checklists").select("*").order("name"),
      supabase.from("hq_checklist_items").select("*").order("sort_order"),
    ]);
    setLists((l as Checklist[]) ?? []);
    setItems((i as ChItem[]) ?? []);
  }

  async function saveList() {
    if (!editing?.name) return;
    const payload = {
      name: editing.name, description: editing.description ?? null,
      frequency: editing.frequency ?? "daily",
      assigned_to_roles: editing.assigned_to_roles ?? ["employee"],
      active: editing.active ?? true,
    };
    if (editing.id) await supabase.from("hq_checklists").update(payload).eq("id", editing.id);
    else await supabase.from("hq_checklists").insert(payload);
    setEditing(null); await load();
  }

  async function addItem(checklistId: string, label: string) {
    if (!label.trim()) return;
    const order = (items.filter(i => i.checklist_id === checklistId).length) + 1;
    await supabase.from("hq_checklist_items").insert({ checklist_id: checklistId, label: label.trim(), sort_order: order, required: true });
    await load();
  }
  async function delItem(id: string) { await supabase.from("hq_checklist_items").delete().eq("id", id); await load(); }
  async function delList(id: string) {
    const list = lists.find(l => l.id === id);
    const ok = await ask({
      title: list ? `Delete "${list.name}"?` : "Delete this checklist?",
      body: "Every item on it goes too, along with the record of who ticked what.",
      confirmLabel: "Delete checklist",
      danger: true,
    });
    if (!ok) return;
    await supabase.from("hq_checklists").delete().eq("id", id); await load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Manage Checklists</h1>
      {dialog}
        <button onClick={() => setEditing({ name: "", frequency: "daily", assigned_to_roles: ["employee"], active: true })} className="btn">+ Checklist</button>
      </div>

      <div className="space-y-3">
        {lists.map(l => {
          const its = items.filter(i => i.checklist_id === l.id);
          return (
            <div key={l.id} className="card">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{l.name} <span className="text-xs text-gray2">({l.frequency})</span></h2>
                  <div className="text-xs text-gray2">{(l.assigned_to_roles ?? []).join(", ")} {l.active ? "" : "· INACTIVE"}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setOpenItems(openItems === l.id ? null : l.id)} className="btn btn-sm btn-ghost">{openItems === l.id ? "Hide" : "Items"} ({its.length})</button>
                  <button onClick={() => setEditing(l)} className="btn btn-sm btn-ghost">Edit</button>
                  <button onClick={() => delList(l.id)} className="btn btn-sm btn-ghost text-bad ring-bad">×</button>
                </div>
              </div>
              {openItems === l.id && (
                <div className="rounded-lg bg-offwhite p-3 space-y-1">
                  {its.map(it => (
                    <div key={it.id} className="flex items-center justify-between gap-2 text-sm">
                      <span>{it.sort_order}. {it.label}</span>
                      <button onClick={() => delItem(it.id)} className="text-xs text-bad hover:underline">remove</button>
                    </div>
                  ))}
                  <NewItemRow onAdd={(label) => addItem(l.id, label)} />
                </div>
              )}
            </div>
          );
        })}
        {lists.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No checklists yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} checklist</h2>
            <div className="space-y-3">
              <div><label className="label">Name</label><input className="input" value={editing.name ?? ""} onChange={e => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><label className="label">Description</label><input className="input" value={editing.description ?? ""} onChange={e => setEditing({ ...editing, description: e.target.value || null })} /></div>
              <div><label className="label">Frequency</label><select className="input" value={editing.frequency ?? "daily"} onChange={e => setEditing({ ...editing, frequency: e.target.value })}>{["once","daily","weekly","shift_open","shift_close"].map(f => <option key={f}>{f}</option>)}</select></div>
              <div>
                <label className="label">Assigned to roles</label>
                <div className="flex flex-wrap gap-3 text-sm">
                  {["employee","admin","accountant","super_admin"].map(r => {
                    const checked = (editing.assigned_to_roles ?? []).includes(r);
                    return <label key={r} className="flex items-center gap-1"><input type="checkbox" checked={checked} onChange={e => {
                      const cur = editing.assigned_to_roles ?? [];
                      setEditing({ ...editing, assigned_to_roles: e.target.checked ? [...cur, r] : cur.filter(x => x !== r) });
                    }} /> {r}</label>;
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
              <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button><button onClick={saveList} className="btn" disabled={!editing.name}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewItemRow({ onAdd }: { onAdd: (label: string) => void }) {
  const [text, setText] = useState("");
  return (
    <div className="flex gap-2 pt-2">
      <input className="input" placeholder="+ New item…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { onAdd(text); setText(""); } }} />
      <button onClick={() => { onAdd(text); setText(""); }} className="btn btn-sm">Add</button>
    </div>
  );
}
